/**
 * The loading view: three counter-rotating rings over a particle field.
 *
 * Ported from the legacy loader (index.html:2394–2412, and the fullscreen copy
 * at :2784). Ring widths, colours, spin durations and directions are unchanged,
 * as are the gradient heading and the hint pill.
 *
 * Three variants, one identity:
 *
 *   full   — the whole thing, particles included. What every page-level load
 *            uses, and what `Spinner` renders by default.
 *   inline — rings and label only, no canvas. For a loader inside a card or a
 *            modal, where a starfield would be louder than the thing it covers.
 *   cover  — full, as a fixed overlay above the app. Boot and route changes.
 *
 * The icon resolves through ./icons rather than the `Icon` wrapper in ./index,
 * because ./index re-exports this file — going through it would be a cycle.
 */

import { resolveIcon } from './icons';
import { ParticleField } from './ParticleField';

const Activity = resolveIcon('activity');

const VARIANTS = {
  full: { box: 'h-28 w-28', icon: 40, title: 'text-3xl' },
  cover: { box: 'h-28 w-28', icon: 40, title: 'text-3xl' },
  inline: { box: 'h-16 w-16', icon: 22, title: 'text-sm' },
};

export const DEFAULT_LOADING_TITLE = 'جاري تحميل العيادة...';
export const DEFAULT_LOADING_HINT = 'حرّك الماوس للتفاعل مع الجزيئات أثناء الانتظار ✨';

/**
 * The rings on their own.
 *
 * Each ring shows a single border edge and turns at its own rate, which is what
 * produces the woven look rather than three circles rotating as one.
 */
export function LoadingRings({ variant = 'full', className = '' }) {
  const { box, icon } = VARIANTS[variant] || VARIANTS.full;
  return (
    <div className={`relative ${box} ${className}`} aria-hidden="true">
      <div className="absolute inset-0 animate-[spin_2s_linear_infinite] rounded-full border-t-4 border-teal-500" />
      <div className="absolute inset-2 animate-[spin_1.5s_linear_infinite_reverse] rounded-full border-s-4 border-cyan-400" />
      <div className="absolute inset-4 animate-[spin_3s_linear_infinite] rounded-full border-b-4 border-purple-500" />
      <div className="absolute inset-0 flex items-center justify-center">
        <Activity
          size={icon}
          className="animate-pulse text-white drop-shadow-[0_0_10px_rgba(45,212,191,0.8)]"
        />
      </div>
    </div>
  );
}

export function LoadingScreen({
  title = DEFAULT_LOADING_TITLE,
  hint = DEFAULT_LOADING_HINT,
  variant = 'full',
  className = '',
}) {
  const { title: titleSize } = VARIANTS[variant] || VARIANTS.full;

  if (variant === 'inline') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`flex flex-col items-center justify-center gap-4 py-10 ${className}`}
      >
        <LoadingRings variant="inline" />
        {title && <p className={`${titleSize} text-slate-400`}>{title}</p>}
      </div>
    );
  }

  const body = (
    <>
      <ParticleField />

      {/* pointer-events-none so the canvas underneath still gets the pointer —
          without it the hint would be a lie, since the text sits over the middle
          of the field where most of the interaction happens. */}
      <div className="pointer-events-none relative z-10 flex flex-col items-center justify-center px-6 text-center">
        <LoadingRings variant={variant} className="mb-8" />

        {title && (
          <h3
            className={`${titleSize} mb-3 bg-gradient-to-r from-teal-400 to-cyan-300 bg-clip-text font-black text-transparent drop-shadow-sm`}
          >
            {title}
          </h3>
        )}

        {hint && (
          <p className="animate-pulse rounded-full border border-white/5 bg-slate-900/50 px-4 py-2 text-sm font-medium text-slate-400 backdrop-blur-md">
            {hint}
          </p>
        )}
      </div>
    </>
  );

  if (variant === 'cover') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`fixed inset-0 z-[150] flex flex-col items-center justify-center bg-slate-950 ${className}`}
      >
        {body}
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative flex min-h-[60vh] w-full flex-col items-center justify-center overflow-hidden rounded-3xl border border-white/5 bg-slate-900 shadow-2xl ${className}`}
    >
      {body}
    </div>
  );
}

export default LoadingScreen;
